import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatasetFileTypeOption, FileAction, FileModel, Folder, FolderViewModel, ItemsToBeRemovedOptions, MedResultItemVM } from '@ca/core/models';
import { FilePreviewService, WindowRef } from '@ca/core/services';
import { ArivisService } from 'src/app/core/services/arivis.service';
import { FileExplorerMode } from '../../../core/types/file-explorer-mode.type';
import { FILE_EXPLORER_MODE } from '../../constants/constants';

@Component({
    selector: 'ca-results-file-actions',
    templateUrl: './result-file-actions.component.html',
    styleUrls: ['./results-file-actions.component.scss'],
})
export class ResultsFileActionsComponent {
    @Input() set actions(actions: FileAction[]) {
        this._actions = actions;
        this.isDownloadBtnEnabled = this.actions.includes('download');
        this.isRenameBtnEnabled = this.actions.includes('rename');
        this.isViewBtnEnabled = this.actions.includes('view');
        this.isDeleteBtnEnabled = this.actions.includes('delete');
        this.isRemoveBtnEnabled = this.actions.includes('remove');
    }
    @Input() index: number = 0;
    @Input() isFolder: boolean;
    @Input() isSelected: boolean;
    @Input() mode: FileExplorerMode = FILE_EXPLORER_MODE.MODIFICATION;
    @Input() set item(item) {
        this._item = item;
    }
    @Input() datasetFolderType?: DatasetFileTypeOption;
    @Input() datasetId?: string;
    @Input() textClasses: string[] = [];
    @Input() truncateFileNameAtWidthOf?: number;

    @Input() showFileStatus = false;
    @Output() onOpenFolder = new EventEmitter<string>();
    @Output() deleteItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel & Folder>>();
    @Output() removeItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel & Folder>>();

    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;
    isDownloadBtnEnabled = false;
    isRenameBtnEnabled = false;
    isViewBtnEnabled = false;
    isDeleteBtnEnabled = false;
    isRemoveBtnEnabled = false;

    private _actions: FileAction[] = [];
    private _item;

    get item() {
        return this._item;
    }

    get actions(): FileAction[] {
        return this._actions;
    }

    constructor(
        private filePreviewService: FilePreviewService,
        private arivisService: ArivisService,
        private windowRef: WindowRef,
    ) {}

    /**
     * Determines if the file type is supported by Arivis viewer.
     * @param item The file model to check.
     * @returns True if supported, false otherwise.
     */
    isSupportedByArivis(item: FileModel): boolean {
        return this.arivisService.isSupportedByArivis(item.type);
    }

    /**
     * Checks if the file type is supported by the file previewer.
     * @param type The MIME type or file extension.
     * @returns True if supported, false otherwise.
     */
    isSupportedByFilePreviewer(type: string | undefined): boolean {
        return this.arivisService.isSupportedByFilePreviewer(type);
    }

    /**
     * Opens a file or folder item based on the current mode.
     * @param item The file or folder item.
     * @param event The triggered event.
     */
    openItem(item: FileModel | Folder | FolderViewModel | MedResultItemVM, event: Event): void {
        if (this.mode !== FILE_EXPLORER_MODE.SELECTION) {
            event.stopPropagation();
            if (this.isFolder) {
                this.onOpenFolder.emit(item.id);
            } else {
                this.openFile(item as FileModel);
            }
        }
    }

    /**
     * Opens a file using the appropriate viewer or previewer.
     * @param file The file model to open.
     */
    openFile(file: FileModel): void {
        if (this.actions.includes('view')) {
            if (this.isSupportedByArivis(file)) {
                this.openViewerUrl(file);
            }
            if (this.isSupportedByFilePreviewer(file.type!)) {
                this.filePreviewService.openWithURL(file.token, file.type!);
            }
        }
    }

    /**
     * Opens the file in the Arivis viewer.
     * @param file The file model to view.
     */
    private openViewerUrl(file: FileModel): void {
        if (file.viewerUrl == null) {
            file.viewerUrl = this.arivisService.generateArivisMlViewerUrl(file);
        }

        let url = file.viewerUrl;
        if (this.datasetId != null && url != null) {
            if (this.datasetFolderType === DatasetFileTypeOption.FILES) {
                url += `&datasetId=${this.datasetId}`;
            }
        }
        this.windowRef.nativeWindow.open(url, '_blank');
    }
}
